import { useState } from "react";
import { AlertTriangle, Check, Clock, Plus, Repeat } from "lucide-react";
import { catStyle } from "@/lib/constants";
import { fmtDeadline, fmtDuration, toDay } from "@/lib/time";

function TaskRow({ task, onOpen, onToggle }) {
  const c = catStyle(task.category);
  const done = task.status === "done";
  return (
    <li className="group flex items-start gap-3 rounded-xl p-3 hover:bg-[#F5F2EB] transition-colors duration-200" data-testid={`backlog-task-${task.id}`}>
      <button
        onClick={() => onToggle(task)}
        data-testid={`task-done-toggle-${task.id}`}
        aria-label={done ? "Mark as not done" : "Mark as done"}
        className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border flex items-center justify-center transition-colors duration-200 ${done ? "bg-[#103080] border-[#103080] text-white" : "border-[#103080]/30 hover:border-[#0090D0]"}`}
      >
        {done && <Check size={12} />}
      </button>
      <button onClick={() => onOpen(task)} className="flex-1 min-w-0 text-left" data-testid={`backlog-task-open-${task.id}`}>
        <p className={`text-sm font-semibold text-[#103080] truncate ${done ? "line-through opacity-50" : ""}`}>{task.title}</p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#0F172A]/60">
          <span className="rounded-full px-2 py-0.5 font-medium" style={{ background: c.bg, color: c.text }}>{task.category}</span>
          {task.client_name && <span className="truncate max-w-[140px]">{task.client_name}</span>}
          <span className="inline-flex items-center gap-1"><Clock size={11} />{fmtDuration(task.duration_minutes)}</span>
          <span>Due {fmtDeadline(task.deadline)}</span>
          {task.job_id && <span className="inline-flex items-center gap-1 text-[#0090D0]" data-testid={`task-recurring-${task.id}`}><Repeat size={11} />Recurring</span>}
          {!done && task.start_date && task.start_date > toDay(new Date()) && <span>Starts {fmtDeadline(task.start_date)}</span>}
        </div>
        {!done && task.at_risk && (
          <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700" data-testid={`task-risk-${task.id}`}>
            <AlertTriangle size={11} />
            {task.unscheduled_minutes > 0 ? `${fmtDuration(task.unscheduled_minutes)} doesn't fit this week` : "Finishes after deadline"}
          </p>
        )}
      </button>
    </li>
  );
}

export default function TaskBacklog({ tasks, onOpen, onToggle, onAdd }) {
  const [showDone, setShowDone] = useState(false);
  const open = tasks.filter((t) => t.status === "todo");
  const done = tasks.filter((t) => t.status === "done");
  const list = showDone ? done : open;
  const hours = open.reduce((s, t) => s + t.duration_minutes, 0);
  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6 fade-up" data-testid="task-backlog">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#0090D0] font-semibold">Tasks</p>
          <h2 className="font-serif-brand font-semibold text-xl text-[#103080] mt-1">{open.length} open · {fmtDuration(hours)}</h2>
        </div>
        <button onClick={onAdd} data-testid="backlog-add-task-button" aria-label="Add task"
          className="w-9 h-9 rounded-full bg-[#103080] text-white flex items-center justify-center hover:bg-[#0090D0] transition-colors duration-200">
          <Plus size={16} />
        </button>
      </div>
      <div className="inline-flex rounded-full bg-[#F5F2EB] p-1 mb-3">
        {[["To do", false], [`Done (${done.length})`, true]].map(([label, v]) => (
          <button key={label} onClick={() => setShowDone(v)} data-testid={`backlog-tab-${v ? "done" : "todo"}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors duration-200 ${showDone === v ? "bg-white text-[#103080] shadow-sm" : "text-[#103080]/60"}`}>
            {label}
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-[#0F172A]/50 py-6 text-center" data-testid="backlog-empty">
          {showDone ? "Nothing completed yet." : "No open tasks. Add one to start planning your week."}
        </p>
      ) : (
        <ul className="space-y-1 max-h-[520px] overflow-y-auto thin-scroll -mx-3">
          {list.map((t) => <TaskRow key={t.id} task={t} onOpen={onOpen} onToggle={onToggle} />)}
        </ul>
      )}
    </section>
  );
}
